// Round 97c (Feb 27, 2026) — 7-year revenue trajectory chip for the
// StockTable. Compact inline SVG sparkline normalised to the row's
// vertical rhythm. Color reflects direction (green if last > first,
// red if collapsing, slate if mostly flat). Hover title shows the
// underlying numbers so admins/power-users can verify at a glance
// without opening the modal's Growth tab.

const SECTOR_WIDTH = 56; // px — keeps every chip the same footprint
const SECTOR_HEIGHT = 14;
const BAR_GAP = 1;

function fmtUsd(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export default function RevenueSparkline({ history, ticker, onClick }) {
  if (!Array.isArray(history) || history.length < 3) return null;

  const values = history.map((h) => h.value || 0);
  const maxV = Math.max(...values);
  if (maxV <= 0) return null;

  const first = values[0];
  const last = values[values.length - 1];
  // Direction: > +20% growth from first to last → emerald,
  // < -10% → rose, else slate (flat/mixed).
  const delta = first > 0 ? (last - first) / Math.abs(first) : 0;
  let barFill;
  let lastFill;
  if (delta >= 0.20) {
    barFill = 'fill-emerald-400/40 dark:fill-emerald-500/40';
    lastFill = 'fill-emerald-500 dark:fill-emerald-400';
  } else if (delta <= -0.10) {
    barFill = 'fill-rose-400/40 dark:fill-rose-500/40';
    lastFill = 'fill-rose-500 dark:fill-rose-400';
  } else {
    barFill = 'fill-slate-400/40 dark:fill-slate-500/40';
    lastFill = 'fill-slate-600 dark:fill-slate-300';
  }

  const n = values.length;
  const barW = (SECTOR_WIDTH - BAR_GAP * (n - 1)) / n;

  const titleLines = history
    .map((h) => `${h.year}: ${fmtUsd(h.value)}`)
    .join('\n');
  const arrow = delta >= 0.05 ? '↗' : delta <= -0.05 ? '↘' : '→';
  const pctTxt = `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(0)}%`;

  return (
    <button
      type="button"
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
      className="inline-flex items-center align-middle hover:brightness-110 transition-[filter]"
      title={`Revenue ${history[0].year}-${history[history.length - 1].year}\n${titleLines}\n${arrow} ${pctTxt} over ${history.length - 1}y`}
      data-testid={`revenue-sparkline-${ticker}`}
      aria-label={`7-year revenue trajectory: ${arrow} ${pctTxt}`}
    >
      <svg
        width={SECTOR_WIDTH}
        height={SECTOR_HEIGHT}
        viewBox={`0 0 ${SECTOR_WIDTH} ${SECTOR_HEIGHT}`}
        className="overflow-visible"
      >
        {values.map((v, i) => {
          const h = Math.max(2, (v / maxV) * SECTOR_HEIGHT);
          const x = i * (barW + BAR_GAP);
          const y = SECTOR_HEIGHT - h;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barW}
              height={h}
              className={i === n - 1 ? lastFill : barFill}
              rx={0.5}
            />
          );
        })}
      </svg>
    </button>
  );
}
